import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Printer, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const LETTERS = "ABCDEFGHIJ";
const fmt = (d) => { const dt = new Date(d + "T12:00:00"); return `${DAYS[dt.getDay()]}, ${dt.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`; };

function Ball({ n, red }) {
  return <span className={`inline-flex items-center justify-center w-9 h-9 rounded-full border-2 font-mono font-bold text-sm ${red ? "border-red-600 text-red-600" : "border-black text-black"}`}>{String(n).padStart(2, "0")}</span>;
}

export default function PlaySlip() {
  const [picks, setPicks] = useState(null);
  const [scope, setScope] = useState("upcoming");
  const [powerPlay, setPowerPlay] = useState(false);
  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    api.get("/picks").then((r) => setPicks(r.data.picks)).catch(() => setPicks([]));
  }, []);

  const groups = useMemo(() => {
    if (!picks) return [];
    const list = scope === "upcoming" ? picks.filter((p) => p.target_draw_date && p.target_draw_date >= today) : picks;
    const by = {};
    list.forEach((p) => { const k = p.target_draw_date || "next"; (by[k] = by[k] || []).push(p); });
    return Object.entries(by).sort(([a], [b]) => (a === "next" ? 1 : b === "next" ? -1 : a.localeCompare(b)));
  }, [picks, scope, today]);
  const total = groups.reduce((n, [, ps]) => n + ps.length, 0);

  return (
    <div className="min-h-screen bg-white text-black print:bg-white" data-testid="play-slip-page">
      <div className="print:hidden bg-ink text-parchment px-4 sm:px-8 py-4 flex flex-wrap items-center justify-between gap-3">
        <Link to="/" data-testid="slip-back-link" className="label-caps text-parchment/60 hover:text-oracle inline-flex items-center gap-2"><ArrowLeft className="w-3 h-3" /> Back</Link>
        <div className="flex items-center gap-3">
          <div className="flex rounded-md border border-white/10 overflow-hidden" data-testid="slip-scope">
            <button data-testid="slip-scope-upcoming" onClick={() => setScope("upcoming")} className={`label-caps px-3 py-2 ${scope === "upcoming" ? "bg-oracle text-ink" : "text-parchment/60"}`}>Upcoming draws</button>
            <button data-testid="slip-scope-all" onClick={() => setScope("all")} className={`label-caps px-3 py-2 ${scope === "all" ? "bg-oracle text-ink" : "text-parchment/60"}`}>All saved picks</button>
          </div>
          <label className="label-caps flex items-center gap-2 text-parchment/70 cursor-pointer"><input data-testid="slip-powerplay" type="checkbox" checked={powerPlay} onChange={(e) => setPowerPlay(e.target.checked)} className="accent-[#FFB000]" /> Power Play</label>
          <Button data-testid="slip-print-btn" onClick={() => window.print()} disabled={!total} className="bg-oracle text-ink hover:bg-[#FFC033] label-caps"><Printer className="w-4 h-4 mr-2" /> Print slip</Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8 print:px-0 print:py-0">
        <div className="flex items-end justify-between border-b-2 border-black pb-3">
          <div>
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase">Powerball Oracle · Play Slip</div>
            <h1 className="font-heading text-4xl mt-1">Your numbers, <em>ready to play.</em></h1>
          </div>
          <div className="font-mono text-[10px] text-right leading-relaxed">
            Printed {new Date().toLocaleDateString()}<br />{total} play{total === 1 ? "" : "s"} · ${(total * (powerPlay ? 3 : 2)).toFixed(2)} at $
            {powerPlay ? "3" : "2"} each
          </div>
        </div>

        {picks === null && <div className="py-16 text-center font-mono text-xs">Loading…</div>}
        {picks !== null && total === 0 && (
          <div data-testid="slip-empty" className="py-16 text-center font-mono text-xs">
            No {scope === "upcoming" ? "picks aimed at upcoming draws" : "saved picks"} yet. Cast a week of picks in the Oracle and save them.
          </div>
        )}

        {groups.map(([date, ps]) => (
          <section key={date} data-testid={`slip-group-${date}`} className="mt-8 break-inside-avoid">
            <div className="flex items-baseline justify-between">
              <h2 className="font-heading text-2xl">{date === "next" ? "Next drawing" : fmt(date)}</h2>
              <span className="font-mono text-[10px] uppercase tracking-widest">Draw 10:59 PM ET{powerPlay ? " · Power Play ✓" : ""}</span>
            </div>
            <div className="mt-3 border border-black">
              {ps.map((p, i) => (
                <div key={p.id} data-testid={`slip-line-${p.id}`} className="grid grid-cols-12 items-center px-3 py-2.5 border-b border-black/20 last:border-0">
                  <div className="col-span-1 font-heading text-2xl">{LETTERS[i] || i + 1}</div>
                  <div className="col-span-8 flex items-center gap-1.5">
                    {[...p.whites].sort((a, b) => a - b).map((n) => <Ball key={n} n={n} />)}
                    <span className="mx-1 font-mono text-xs">PB</span>
                    <Ball n={p.powerball} red />
                  </div>
                  <div className="col-span-3 text-right font-mono text-[10px] uppercase tracking-wider text-black/60">{p.strategy.replace(/_/g, " ")}</div>
                </div>
              ))}
            </div>
          </section>
        ))}

        {total > 0 && (
          <div className="mt-8 border-t border-black/30 pt-4 font-mono text-[10px] leading-relaxed text-black/70 break-inside-avoid">
            <strong>At the counter:</strong> on a Powerball slip, for each play (A, B, C…) mark the five white numbers 1–69 and one red Powerball 1–26 exactly as printed, tick the draw date shown, and optionally Power Play. $2 per play ($3 with Power Play). Check your ticket before leaving. Must be 18+ (21+ in some states). Powerball draws are random — play responsibly.
          </div>
        )}
      </div>
    </div>
  );
}
